import { observer } from "mobx-react";
import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  TextInput,
  Paper,
  Group,
  Button,
  Text,
  Code,
  Overlay,
  Box,
  Alert,
  Anchor,
} from "@mantine/core";

import { MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS } from "../../../helpers/constants";
import { BOB_MODALS } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";
import { IconAlertTriangle, IconAt } from "@tabler/icons-react";
import TwoFactorInput from "../details/twoFactorInput";
import { CustomModalTitle } from "../details/customModalTitle";

const EditAliasModal = observer(({ bob }: { bob: Bob }) => {
  const [twoFACode, setTwoFACode] = useState<string>("");

  const handleClose = () => {
    bob.setUserProposedAlias(bob.alias); // Reset proposed alias to current alias
    bob.setOpenModal(null);
  };

  useEffect(() => {
    if (bob.openModal === BOB_MODALS.EDIT_ALIAS) {
      bob.setUserProposedAlias(bob.alias); // Set proposed alias to current alias when modal opens
    }
  }, [bob.openModal]);
  const onSave = () => {
    bob.sendDataAndDirectiveToRails("try-to-modify-alias", {
      proposedAlias: bob.userProposedAlias.trim(),
      twoFactorCode: twoFACode.trim() || null,
    });
  };

  // Validate alias - must be URL safe, no spaces, no special chars except _ and -,
  // valid as first part of email, more than 2 characters
  const isValidAlias = (alias: string) => {
    if (!alias || alias.trim().length < 2) {
      return false;
    }

    const trimmedAlias = alias.trim();

    // Check for valid characters only (a-z, 0-9, _, -)
    const validCharactersRegex = /^[a-z0-9_-]+$/i;
    if (!validCharactersRegex.test(trimmedAlias)) {
      return false;
    }

    // Must not start or end with underscore or hyphen
    if (
      trimmedAlias.startsWith("_") ||
      trimmedAlias.startsWith("-") ||
      trimmedAlias.endsWith("_") ||
      trimmedAlias.endsWith("-")
    ) {
      return false;
    }

    // Check if it would be valid as the first part of an email
    const emailRegex = /^[a-zA-Z0-9_-]+$/;
    return emailRegex.test(trimmedAlias);
  };

  const getValidationError = (alias: string) => {
    if (!alias || alias.trim().length === 0) {
      return "Alias is required";
    }

    const trimmedAlias = alias.trim();

    if (trimmedAlias.length < 2) {
      return "Alias must be at least 2 characters long";
    }

    if (trimmedAlias.includes(" ")) {
      return "Alias cannot contain spaces";
    }

    if (!/^[a-z0-9_-]+$/i.test(trimmedAlias)) {
      return "Alias can only contain letters, numbers, underscores, and hyphens";
    }

    if (
      trimmedAlias.startsWith("_") ||
      trimmedAlias.startsWith("-") ||
      trimmedAlias.endsWith("_") ||
      trimmedAlias.endsWith("-")
    ) {
      return "Alias cannot start or end with underscore or hyphen";
    }

    return undefined;
  };

  // Check if save should be enabled
  const isSaveEnabled = () => {
    const trimmedAlias = bob.userProposedAlias.trim();
    return isValidAlias(trimmedAlias) && trimmedAlias !== bob.alias;
  };

  const handleAliasChange = (proposedAlias: string) => {
    bob.setUserProposedAlias(proposedAlias);
  };

  const validationError = getValidationError(bob.userProposedAlias);

  return (
    <Modal
      opened={bob.openModal === BOB_MODALS.EDIT_ALIAS}
      onClose={handleClose}
      size={MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS}
      title={
        <CustomModalTitle
          text="Change Lightning Address"
          icon={<IconAt size={24} />}
        />
      }
    >
      <Paper withBorder shadow="md" p={24} radius="md">
        <Stack>
          <TextInput
            label="Change Your Lightning Address"
            placeholder="your-alias"
            value={bob.userProposedAlias}
            disabled={bob.areWeWaitingOnRails}
            maxLength={40}
            //description="Enter a unique alias that will be part of your Lightning address. Only letters, numbers, underscores, and hyphens are allowed."
            onChange={(e) => handleAliasChange(e.currentTarget.value)}
            error={validationError}
          />

          <Text size="sm" c="dimmed" mb="xs">
            {validationError
              ? "Invalid address"
              : "Your Lightning address will be:"}
          </Text>
          <Box pos="relative">
            <Code
              block
              p="sm"
              style={{
                fontSize: "14px",
                fontFamily: "monospace",
              }}
            >
              {bob.userProposedAlias.trim() || "your-alias"}@rizful.com
            </Code>
            {validationError && (
              <Overlay color="#000" backgroundOpacity={0.6} />
            )}
          </Box>

          {isSaveEnabled() && (
            <Alert
              icon={<IconAlertTriangle />}
              title="Warning"
              color="red"
              radius="md"
            >
              <Text>
                If you change your Lightning address, payments to your old
                address will fail.{" "}
              </Text>

              <Text>
                {" "}
                If your existing Lightning address is already in use, Consider{" "}
                <Anchor
                  onClick={() => {
                    bob.setOpenModal(BOB_MODALS.CREATE_NEW_BOB);
                  }}
                >
                  making a new vault
                </Anchor>{" "}
                with a new Lightning address instead.
              </Text>
            </Alert>
          )}
          <TwoFactorInput
            visible={bob.workspace.accountRequiresTwoFactorFormForActions}
            code={twoFACode}
            onCodeChange={setTwoFACode}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleClose}>
              Exit
            </Button>
            <Button
              onClick={onSave}
              disabled={bob.areWeWaitingOnRails || !isSaveEnabled()}
              loading={bob.areWeWaitingOnRails}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Modal>
  );
});

export default EditAliasModal;
